/**
 * Pattern: Uno Reverse / Break-and-Retest (Rake Trades)
 *
 * Rake's framework principle: a broken level becomes flipped support /
 * resistance. Detection:
 *   1. Find a recent swing (high or low) within last N bars
 *   2. That swing was BROKEN — price closed past it in subsequent bars
 *   3. Entry is at a RETEST of the flipped level (within X ticks)
 *      AND the trade direction aligns with the flip:
 *        - Broken swing HIGH → flipped to support → entry should be LONG
 *          (testing the broken level from above)
 *        - Broken swing LOW → flipped to resistance → entry should be SHORT
 *
 * Returns null when the conditions don't all align. When fired, the
 * trade is well-aligned with Rake's "level-to-level" thesis.
 *
 * Pure function. No side effects.
 */

import { detectSwings } from '../swingDetect.js';

const normalizeDirection = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (s === 'long' || s === 'buy' || s === 'l') return 'long';
  if (s === 'short' || s === 'sell' || s === 's') return 'short';
  return null;
};

const findEntryBarIdx = (bars, entryTs) => {
  if (!Array.isArray(bars) || bars.length === 0) return -1;
  let idx = -1;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= entryTs) idx = i;
    else break;
  }
  return idx;
};

const simpleATR = (bars, endIdx, period = 14) => {
  if (endIdx < period) return null;
  let sum = 0, count = 0;
  for (let i = endIdx - period + 1; i <= endIdx; i++) {
    const cur = bars[i];
    const prev = bars[i - 1];
    if (!cur || !prev) continue;
    const hi = Number(cur.high);
    const lo = Number(cur.low);
    const pc = Number(prev.close);
    if (![hi, lo, pc].every(Number.isFinite)) continue;
    sum += Math.max(hi - lo, Math.abs(hi - pc), Math.abs(lo - pc));
    count++;
  }
  return count > 0 ? sum / count : null;
};

export const UNO_REVERSE_LOOKBACK_BARS  = 30;
export const UNO_REVERSE_MAX_DIST_ATR   = 0.30;

export const detectUnoReverse = ({ bars, entryTs, entryPrice, direction, options = {} } = {}) => {
  const dir = normalizeDirection(direction);
  if (!dir) return null;
  if (!Array.isArray(bars) || bars.length === 0) return null;
  if (!Number.isFinite(entryTs) || !Number.isFinite(entryPrice)) return null;

  const lookback = options.lookbackBars || UNO_REVERSE_LOOKBACK_BARS;
  const maxDistAtr = options.maxDistanceAtr || UNO_REVERSE_MAX_DIST_ATR;

  const entryIdx = findEntryBarIdx(bars, entryTs);
  if (entryIdx < 10) return null;  // need at least some history

  const atr = simpleATR(bars, entryIdx);
  if (!Number.isFinite(atr) || atr <= 0) return null;

  // Detect swings in the lookback window
  const windowStart = Math.max(0, entryIdx - lookback);
  const windowBars = bars.slice(windowStart, entryIdx + 1);
  const swings = detectSwings(windowBars);

  // Look for the most recent BROKEN swing whose retest aligns with trade direction
  for (let i = swings.length - 1; i >= 0; i--) {
    const swing = swings[i];
    // Re-map swing.index from windowBars index to bars index
    const globalIdx = swing.index + windowStart;
    if (globalIdx >= entryIdx) continue;

    // Was this swing BROKEN before entry? Check bars after the swing for
    // a close past it in the direction of the trade.
    const swingPrice = swing.price;
    let broken = false;
    for (let j = globalIdx + 1; j < entryIdx; j++) {
      const close = Number(bars[j]?.close);
      if (!Number.isFinite(close)) continue;
      if (swing.type === 'high' && close > swingPrice) { broken = true; break; }
      if (swing.type === 'low'  && close < swingPrice) { broken = true; break; }
    }
    if (!broken) continue;

    // Is the trade direction aligned with the flip?
    //   broken high → flipped to SUPPORT → entry should be LONG
    //   broken low  → flipped to RESISTANCE → entry should be SHORT
    const expectedDir = swing.type === 'high' ? 'long' : 'short';
    if (expectedDir !== dir) continue;

    // Is the entry WITHIN max distance of the flipped level?
    const distance = Math.abs(entryPrice - swingPrice);
    const distanceAtr = distance / atr;
    if (distanceAtr > maxDistAtr) continue;

    return {
      type: 'uno-reverse',
      score: 1,
      maxScore: 1,
      aligned: true,
      details: `Retest of broken swing ${swing.type} at ${swingPrice} (flipped to ${swing.type === 'high' ? 'support' : 'resistance'}). Entry within ${distanceAtr.toFixed(2)}× ATR.`,
      evidence: {
        swingType: swing.type,
        swingPrice,
        swingIndex: globalIdx,
        entryPrice,
        distanceAtr,
        atr,
      },
    };
  }

  return null;
};
